import React, { useState, useEffect, useCallback } from "react";
import { Globe2, Info, RefreshCw, Loader2, TrendingUp, Percent, Flame, Gauge, Landmark, Droplet, Layers, Zap, AlertTriangle, ShieldAlert, Maximize2, X, Download, LineChart as LineChartIcon } from "lucide-react";
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, LineChart, ReferenceLine, ReferenceArea } from "recharts";
import { macroIndicators, shareUpload } from "@/lib/api";
import { downloadSvgJpg, getSvgJpgBlob } from "@/lib/chartExport";
import ShareMenu from "@/components/ShareMenu";
import HoverTip from "@/components/HoverTip";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { CHART_TIP } from "@/lib/tipStyle";

const ICONS = {
    equities: TrendingUp,
    gdp: Landmark,
    fed_rate: Percent,
    inflation: Flame,
    productivity: Gauge,
    m3_proxy: Layers,
    oil: Droplet,
    high_yield_spread: ShieldAlert,
    debt_to_gdp: Landmark,
};

const nf = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 });
const fmtVal = (v) => (v == null ? "—" : nf.format(v));
const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
};

const signedPct = (v) => (v == null ? null : `${v > 0 ? "+" : ""}${nf.format(v)}%`);

// Trailing average of monthly oil history over `years` (shared by oil card + coefficient).
const oilAverage = (history, years) => {
    if (!history || !history.length) return null;
    const n = Math.min(years * 12, history.length);
    const slice = history.slice(history.length - n);
    if (!slice.length) return null;
    return slice.reduce((s, x) => s + x.value, 0) / slice.length;
};

// GDP's live (extrapolated) nowcast for the current quarter; falls back to the
// official value when no estimate is needed. Equities needs no such fallback —
// Total US market (ind.value, ITOT × factor on the Wilshire 5000 scale) is real, updated every trading day.
const liveGdp = (ind) => ind?.live?.value ?? ind?.value;

// High-yield credit spread → continuous, S-shaped effect on the coefficient (see
// backend services/macro.py::_hy_spread_effect for the mirrored, authoritative
// version). Symmetric around HY_NEUTRAL (3.0 p.p., exactly 0 there): moving right,
// the coefficient falls, accelerating until HY_CAP (3.5 p.p. — steepest point), then
// keeps falling but decelerating; moving left mirrors this exactly, accelerating
// until HY_FLOOR (2.5 p.p.). Depends only on the current spread value — a fixed,
// steep S-curve (no persistence/history).
const HY_NEUTRAL = 3.0, HY_CAP = 3.5, HY_FLOOR = 2 * HY_NEUTRAL - HY_CAP, HY_MAX_EFFECT = 0.15;
const HY_K = 5.5;

const hyRightHalf = (spread) => {
    const s0 = 1 / (1 + Math.exp(-HY_K * (HY_NEUTRAL - HY_CAP)));
    const s = 1 / (1 + Math.exp(-HY_K * (spread - HY_CAP)));
    return s0 - s;
};

const hySpreadEffect = (hy) => {
    const value = hy?.value;
    if (value == null) return { effect: 0, value: null };
    const raw = value >= HY_NEUTRAL ? hyRightHalf(value) : -hyRightHalf(2 * HY_NEUTRAL - value);
    const s0 = 1 / (1 + Math.exp(-HY_K * (HY_NEUTRAL - HY_CAP)));
    const bound = Math.max(1 - s0, 1e-9);
    const effect = Math.max(-1, Math.min(1, raw / bound));
    return { effect, value };
};

// Coefficient formula (user-defined):
// C = (m71/(m70-m72)) * (1-(m73+m74)/100) * (m75/100) * (1-((m76-m77)*(m78/10000))) * t5
// m70 Renta variable · m71 PIB · m72 M3 proxy · m73 Tipo FED · m74 Inflación
// m75 Productividad · m76 Precio petróleo · m77 Media petróleo (dial) · m78 Mix petróleo+gas
// m79 Spread high yield (t5, no lineal — ver hySpreadEffect)
const computeCoefficient = (byKey, oilYears) => {
    const g = (k) => byKey[k]?.value;
    const m70 = g("equities"), m71 = liveGdp(byKey["gdp"]),
        m72 = g("m3_proxy"), m73 = g("fed_rate"),
        m74 = g("inflation"), m75 = g("productivity"), m76 = g("oil_avg"), m78 = g("energy_mix");
    const m77 = oilAverage(byKey["oil_avg"]?.history, oilYears);
    const hy = hySpreadEffect(byKey["high_yield_spread"]);
    const m79 = hy.value;
    const vals = { m70, m71, m72, m73, m74, m75, m76, m77, m78, m79 };
    if (Object.values(vals).some((v) => v == null) || m70 - m72 === 0) return null;
    const t1 = m71 / (m70 - m72);
    const t2 = 1 - (m73 + m74) / 100;
    const t3 = m75 / 100;
    const t4 = 1 - ((m76 - m77) * (m78 / 10000));
    const t5 = 1 + HY_MAX_EFFECT * hy.effect;
    return { C: t1 * t2 * t3 * t4 * t5, terms: { t1, t2, t3, t4, t5 }, vals, hy };
};

// Per-indicator secondary line (context value, in its native unit/currency).
const extraLine = (ind) => {
    const e = ind.extra || {};
    switch (ind.key) {
        case "equities":
        case "gdp":
        case "productivity":
        case "m3_proxy":
            return e.yoy_pct != null ? `Crecimiento interanual: ${signedPct(e.yoy_pct)}` : null;
        case "inflation":
            return e.index_value != null ? `Índice CPI: ${nf.format(e.index_value)} (${e.index_base})` : null;
        case "oil":
            return e.change_30d_pct != null ? `~30 días: ${signedPct(e.change_30d_pct)}` : null;
        default:
            return null;
    }
};

// Generic per-indicator 10y evolution chart — the mini-chart on a card + Maximize2
// expand + modal with share/download, same convention as HighYieldSpreadCard/
// DebtCard/OilAverageCard. Used by any card whose indicator carries its own
// `history` but doesn't already have a dedicated chart component. Every point is a
// real reading and gets a dot EXCEPT one flagged `estimated` (a live/extrapolated
// point with no real reading behind it, e.g. productivity's current-quarter nowcast
// or the whole of m3_proxy) — those still sit on the connecting line, just without
// a marker, so the line stays "the estimate" per the product's own definition.
const IndHistoryTip = ({ active, payload, unit }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className={CHART_TIP} data-testid="ind-history-tooltip">
            <div className="font-semibold">{dLabel(p.date, { full: true })}{p.estimated ? " · estimado" : ""}</div>
            <div className="tabular-nums">{nf.format(p.value)} {unit}</div>
        </div>
    );
};

const historyDot = (small) => (props) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null || payload?.estimated) return <g key={index} />;
    return <circle key={index} cx={cx} cy={cy} r={small ? 1.5 : 2.5} fill="var(--chart-blue)" />;
};

const IndHistoryChart = ({ history, unit, height, small, tickFormatter = qLabel }) => {
    if (!history?.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={history} margin={{ top: 4, right: small ? 4 : 40, left: small ? -22 : 0, bottom: 0 }}>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <XAxis dataKey="date" tickFormatter={tickFormatter} tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} interval={Math.max(0, Math.ceil(history.length / (small ? 4 : 10)) - 1)} axisLine={{ stroke: "#00000022" }} tickLine={false} minTickGap={small ? 12 : 20} />
                <YAxis tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} width={small ? 30 : 46} axisLine={false} tickLine={false} domain={["auto", "auto"]} tickFormatter={(v) => nf.format(v)} />
                <RTooltip content={<IndHistoryTip unit={unit} />} />
                <Line type="monotone" dataKey="value" stroke="var(--chart-blue)" strokeWidth={2} dot={historyDot(small)} activeDot={{ r: small ? 3 : 4 }} isAnimationActive={false} connectNulls />
            </LineChart>
        </ResponsiveContainer>
    );
};

const yearLabel = (iso) => (iso || "").slice(0, 4);

const IndHistoryModal = ({ ind, onClose }) => {
    const ref = React.useRef(null);
    const [busy, setBusy] = useState(false);
    const history = ind.history || [];
    const Icon = ICONS[ind.key] || Globe2;
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, `${ind.key}-evolucion`); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={`ind-history-modal-${ind.key}`}>
            <div className="bg-white border-2 border-[#052049] w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <Icon size={22} className="shrink-0" /> <span className="truncate">{ind.label} · Evolución</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title={`${ind.label} · Valuation Studio`} testidPrefix={`ind-history-share-${ind.key}`}
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", ind.label)} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid={`ind-history-download-jpg-${ind.key}`} title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid={`ind-history-modal-close-${ind.key}`} aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                <div ref={ref}>
                    <IndHistoryChart history={history} unit={ind.unit} height={420} tickFormatter={ind.key === "energy_mix" ? yearLabel : qLabel} />
                </div>
                <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed">
                    {ind.description} Fuente: {ind.source} · {ind.frequency}.
                    {ind.key === "m3_proxy" && " El histórico es una reconstrucción estimada (no hay dato real de fondos institucionales anterior a hoy)."}
                    {ind.key === "productivity" && ind.estimated && " El último punto es una extrapolación (el dato oficial de ese trimestre aún no se ha publicado)."}
                    {ind.key === "energy_mix" && ind.estimated && " El último punto es una extrapolación (el dato mundial oficial de OWID aún no llega al año en curso)."}
                </p>
            </div>
        </div>
    );
};

const MacroCard = ({ ind }) => {
    const Icon = ICONS[ind.key] || Globe2;
    const extra = extraLine(ind);
    const history = ind.history || [];
    const [histOpen, setHistOpen] = useState(false);
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={`${ind.description}\n\n${ind.interpretation}\n\nFuente: ${ind.source} · ${ind.frequency}${ind.note ? `\n\n${ind.note}` : ""}`}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-info-${ind.key}`} aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                {history.length > 1 && (
                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-expand-${ind.key}`} aria-label="Ampliar histórico" title="Ampliar">
                        <Maximize2 size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid={`macro-value-${ind.key}`}>
                    {fmtVal(ind.value)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit}{ind.estimated ? " · est." : ""}</span>
            </div>

            <div className="text-[11px] text-[#7A7A7A] mt-1.5">{ind.interpretation}</div>
            {extra && <div className="text-[11px] text-[#9A9A9A] mt-1 tabular-nums">{extra}</div>}

            {history.length > 1 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-history-${ind.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <IndHistoryChart history={history} unit={ind.unit} height={90} small tickFormatter={ind.key === "energy_mix" ? yearLabel : qLabel} />
                </div>
            )}

            {ind.components && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-breakdown-${ind.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Desglose (miles de M$)</div>
                    {ind.components.map((c) => (
                        <div key={c.series} className="flex items-center justify-between text-[11px] tabular-nums py-0.5">
                            <span className="text-[#4A4A4A]">
                                {c.label}
                                {c.as_of && <span className="text-[#9A9A9A] ml-1">({fmtDate(c.as_of)})</span>}
                                {c.frozen && <span className="text-[#B8860B] not-italic ml-1" title="Congelado: última lectura válida">⚠ congelado</span>}
                            </span>
                            <span className="text-[#052049] font-medium">{fmtVal(c.value)}</span>
                        </div>
                    ))}
                    {ind.formula && (
                        <div className="text-[10px] text-[#7A7A7A] mt-1.5 leading-snug border-t border-black/5 pt-1.5" data-testid={`macro-formula-${ind.key}`}>
                            {ind.formula}
                        </div>
                    )}
                    {ind.warning && (
                        <div className="mt-2 flex items-start gap-1.5 text-[10px] text-[#8a2318] bg-[#FBE9E7] border border-[#B32A22]/40 p-1.5 leading-snug" data-testid={`macro-warning-${ind.key}`}>
                            <AlertTriangle size={12} className="text-[#B32A22] shrink-0 mt-0.5" />
                            <span>{ind.warning}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                {ind.stale ? (
                    <span className="inline-flex items-center gap-1 text-[#B8860B] font-semibold" data-testid={`macro-stale-${ind.key}`} title="La fuente ya no actualiza esta serie; es el último dato disponible">
                        <AlertTriangle size={12} /> Desactualizada · {fmtDate(ind.as_of)}
                    </span>
                ) : ind.estimated ? (
                    <span className="tabular-nums text-[#052049] font-semibold" data-testid={`macro-asof-${ind.key}`}>Estimado: {fmtDate(ind.as_of)}</span>
                ) : (
                    <span className="tabular-nums text-[#052049] font-semibold" data-testid={`macro-asof-${ind.key}`}>Dato: {fmtDate(ind.as_of)}</span>
                )}
            </div>
            {histOpen && <IndHistoryModal ind={ind} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

const LiveIndicatorCard = ({ ind }) => {
    const Icon = ICONS[ind.key] || Globe2;
    const live = ind.live;
    const hasLive = live?.value != null;
    const history = ind.history || [];
    const [histOpen, setHistOpen] = useState(false);

    const tip = `${ind.description}\n\nEstimación EN VIVO: al último PIB oficial (${fmtDate(ind.as_of)}) se le aplica el crecimiento intertrimestral medio reciente, extrapolado un trimestre cada vez hasta el trimestre en curso (${live ? fmtDate(live.as_of) : "—"}) — nunca prorrateado día a día hasta hoy.\n\nFuente: ${ind.source} · ${ind.frequency}`;

    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={tip}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-info-${ind.key}`} aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                {history.length > 1 && (
                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-expand-${ind.key}`} aria-label="Ampliar histórico" title="Ampliar">
                        <Maximize2 size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid={`macro-value-${ind.key}`}>
                    {fmtVal(hasLive ? live.value : ind.value)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit}{hasLive ? " · est." : ""}</span>
            </div>
            {hasLive && (
                <div className="text-[11px] text-[#7A7A7A] mt-1 tabular-nums" data-testid={`macro-official-${ind.key}`}>
                    Oficial: {fmtVal(ind.value)} {ind.unit} ({fmtDate(ind.as_of)})
                    {live?.yoy_pct != null && <span className="text-[#9A9A9A]"> · interanual {signedPct(live.yoy_pct)}</span>}
                </div>
            )}

            <div className="text-[11px] text-[#7A7A7A] mt-1.5">{ind.interpretation}</div>

            {history.length > 1 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-history-${ind.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <IndHistoryChart history={history} unit={ind.unit} height={90} small />
                </div>
            )}

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold" data-testid={`macro-asof-${ind.key}`}>
                    {hasLive ? `Estimado: ${fmtDate(live.as_of)}` : `Dato: ${fmtDate(ind.as_of)}`}
                </span>
            </div>
            {histOpen && <IndHistoryModal ind={ind} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

const EQUITY_SERIES_ORDER = ["wilshire", "sp500", "nasdaq", "dow"];
const EQUITY_SERIES_COLOR = { wilshire: "var(--chart-blue)", sp500: "#B32A22", nasdaq: "#1F7A3D", dow: "#B8860B" };

// Merges the histories of whichever series are toggled on into one date-aligned
// dataset for a single overlaid chart. With 2+ series active in the default (non-
// absolute) mode, their absolute levels aren't comparable (Wilshire is $bn, the
// others are index points on wildly different scales even from each other), so
// they're rebased to % change from each one's own first point in the combined
// range — with exactly one active, raw values are kept so the axis still reads in
// that series' native unit. In "valor absoluto" mode every series is already in
// (real or estimated) $bn, so no normalization is applied regardless of count.
const mergeEquitySeries = (bySeries, activeKeys, absolute) => {
    const keys = absolute ? activeKeys.filter((k) => bySeries[k]?.history_usd || k === "wilshire") : activeKeys;
    if (!keys.length) return [];
    const maps = {};
    const dateSet = new Set();
    keys.forEach((k) => {
        const hist = absolute ? (bySeries[k]?.history_usd || bySeries[k]?.history) : bySeries[k]?.history;
        const m = new Map();
        (hist || []).forEach((p) => { m.set(p.date, p.value); dateSet.add(p.date); });
        maps[k] = m;
    });
    const dates = [...dateSet].sort();
    const normalize = !absolute && keys.length > 1;
    const baseline = {};
    if (normalize) {
        keys.forEach((k) => {
            for (const d of dates) {
                if (maps[k].has(d)) { baseline[k] = maps[k].get(d); break; }
            }
        });
    }
    return dates.map((d) => {
        const row = { date: d };
        keys.forEach((k) => {
            if (maps[k].has(d)) {
                const v = maps[k].get(d);
                row[k] = normalize && baseline[k] ? +(((v / baseline[k]) - 1) * 100).toFixed(2) : v;
            }
        });
        return row;
    });
};

const EquitiesTip = ({ active, payload, label, normalized, absolute, bySeries }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className={CHART_TIP} data-testid="equities-tooltip">
            <div className="font-semibold mb-1">{dLabel(label, { full: true })}</div>
            {payload.map((p) => {
                const unit = absolute ? "miles de M$" : normalized ? "%" : (bySeries[p.dataKey]?.unit || "");
                return (
                    <div key={p.dataKey} className="flex justify-between gap-3 tabular-nums">
                        <span style={{ color: p.color }}>{bySeries[p.dataKey]?.label || p.dataKey}</span>
                        <span>{p.value == null ? "—" : nf.format(p.value)} {unit}</span>
                    </div>
                );
            })}
        </div>
    );
};

const EquitiesChart = ({ bySeries, activeKeys, absolute, height, small }) => {
    const keys = absolute ? activeKeys.filter((k) => bySeries[k]?.history_usd || k === "wilshire") : activeKeys;
    const normalize = !absolute && keys.length > 1;
    const data = React.useMemo(() => mergeEquitySeries(bySeries, activeKeys, absolute), [bySeries, activeKeys, absolute]);
    if (!data.length) return null;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 4, right: small ? 4 : 40, left: small ? -18 : 0, bottom: 0 }}>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <XAxis dataKey="date" tickFormatter={qLabel} tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} interval={Math.max(0, Math.ceil(data.length / (small ? 4 : 10)) - 1)} axisLine={{ stroke: "#00000022" }} tickLine={false} minTickGap={small ? 12 : 20} />
                <YAxis tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} width={small ? 34 : 48} axisLine={false} tickLine={false} domain={["auto", "auto"]} tickFormatter={(v) => normalize ? `${nf.format(v)}%` : nf.format(v)} />
                <RTooltip content={<EquitiesTip normalized={normalize} absolute={absolute} bySeries={bySeries} />} />
                {!small && keys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} formatter={(k) => bySeries[k]?.label || k} />}
                {keys.map((k) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={EQUITY_SERIES_COLOR[k] || "var(--chart-blue)"} strokeWidth={2} dot={false} activeDot={{ r: small ? 3 : 4 }} connectNulls isAnimationActive={false} />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

// Shared index-toggle + "valor absoluto" control row, used identically by the
// card's mini view and its expanded modal (same convention as the coefficient
// chart's log-scale toggle) — different `testIdPrefix`/`absoluteTestId` per
// instance so both can be queried independently when both are mounted at once.
const EquitySeriesControls = ({ bySeries, activeKeys, absolute, onToggleKey, onToggleAbsolute, testIdPrefix = "equities-series", absoluteTestId = "equities-absolute-toggle" }) => (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-buttons`}>
        {EQUITY_SERIES_ORDER.filter((k) => bySeries[k]).map((k) => {
            const on = activeKeys.includes(k);
            const disabled = absolute && k === "dow";
            return (
                <button
                    key={k}
                    onClick={() => !disabled && onToggleKey(k)}
                    disabled={disabled}
                    aria-pressed={on}
                    title={disabled ? "Dow Jones no tiene una capitalización real (ni siquiera estimable) — no disponible en modo valor absoluto" : undefined}
                    className="text-[10px] uppercase tracking-wide px-2 py-1 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    style={on && !disabled
                        ? { borderColor: EQUITY_SERIES_COLOR[k], backgroundColor: EQUITY_SERIES_COLOR[k], color: "#fff" }
                        : { borderColor: "rgba(0,0,0,0.2)", color: "#4A4A4A" }}
                    data-testid={`${testIdPrefix}-btn-${k}`}
                >
                    {bySeries[k].label}
                </button>
            );
        })}
        <span className="w-px self-stretch bg-black/15 mx-0.5" aria-hidden="true" />
        <button
            onClick={onToggleAbsolute}
            aria-pressed={absolute}
            title="Convierte cada índice activo a miles de M$ en vez de a su unidad nativa o al % normalizado. S&P 500 y NASDAQ 100 son una estimación (no existe una capitalización oficial derivable de su nivel de índice); Dow Jones no se puede convertir."
            className="text-[10px] uppercase tracking-wide px-2 py-1 border transition-colors"
            style={absolute
                ? { borderColor: "#052049", backgroundColor: "#052049", color: "#fff" }
                : { borderColor: "rgba(0,0,0,0.2)", color: "#4A4A4A" }}
            data-testid={absoluteTestId}
        >
            Valor absoluto ($)
        </button>
    </div>
);

const EquitiesHistoryModal = ({ ind, bySeries, activeKeys, absolute, onToggleKey, onToggleAbsolute, onClose }) => {
    const ref = React.useRef(null);
    const [busy, setBusy] = useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, "renta-variable-evolucion"); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="equities-history-modal">
            <div className="bg-white border-2 border-[#052049] w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <TrendingUp size={22} className="shrink-0" /> <span className="truncate">{ind.label} · Evolución</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title={`${ind.label} · Valuation Studio`} testidPrefix="equities-history-share"
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", ind.label)} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="equities-history-download-jpg" title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="equities-history-modal-close" aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                {Object.keys(bySeries).length > 0 && (
                    <div className="mb-3">
                        <EquitySeriesControls
                            bySeries={bySeries} activeKeys={activeKeys} absolute={absolute}
                            onToggleKey={onToggleKey} onToggleAbsolute={onToggleAbsolute}
                            testIdPrefix="equities-history-series" absoluteTestId="equities-history-absolute-toggle"
                        />
                    </div>
                )}
                <div ref={ref}>
                    <EquitiesChart bySeries={bySeries} activeKeys={activeKeys} absolute={absolute} height={420} />
                </div>
                <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed">
                    {ind.description} Fuente: {ind.source} · {ind.frequency}.
                    {absolute
                        ? " En modo valor absoluto, S&P 500 y NASDAQ 100 se muestran en miles de M$ estimados a partir de su capitalización total aproximada (no es una cifra oficial ni en vivo); Dow Jones no tiene esa conversión posible y no aparece en este modo."
                        : (activeKeys.length > 1 && " Con más de un índice activo, las series se normalizan a variación % desde el primer punto del rango para poder compararlas en la misma escala.")}
                </p>
            </div>
        </div>
    );
};

const EquitiesCard = ({ ind }) => {
    const Icon = ICONS[ind.key] || TrendingUp;
    const [activeKeys, setActiveKeys] = useState(["wilshire"]);
    const [absolute, setAbsolute] = useState(false);
    const [histOpen, setHistOpen] = useState(false);
    const bySeries = ind.history_by_series || {};
    const toggle = (k) => setActiveKeys((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
    const toggleAbsolute = () => setAbsolute((v) => !v);

    const tip = `${ind.description}\n\nFuente: ${ind.source} · ${ind.frequency}${ind.note ? `\n\n${ind.note}` : ""}`;

    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={tip}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="macro-info-equities" aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                {Object.keys(bySeries).length > 0 && (
                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="macro-expand-equities" aria-label="Ampliar histórico" title="Ampliar">
                        <Maximize2 size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid="macro-value-equities">
                    {fmtVal(ind.value)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit}</span>
            </div>
            <div className="text-[11px] text-[#7A7A7A] mt-1.5">{ind.interpretation}</div>

            {Object.keys(bySeries).length > 0 && (
                <div className="mt-2">
                    <EquitySeriesControls
                        bySeries={bySeries} activeKeys={activeKeys} absolute={absolute}
                        onToggleKey={toggle} onToggleAbsolute={toggleAbsolute}
                    />
                </div>
            )}
            <div className="text-[10px] text-[#9A9A9A] mt-1 leading-snug" data-testid="equities-series-note">
                El valor y el coeficiente usan siempre el <strong className="text-[#7A7A7A]">mercado total de EEUU</strong> (ETF ITOT en escala Wilshire 5000, dato real diario).
                {absolute
                    ? " En modo valor absoluto, S&P 500 y NASDAQ 100 se muestran en miles de M$ estimados a partir de su capitalización total aproximada (no es una cifra oficial); Dow Jones no está disponible en este modo."
                    : ` Los demás índices son solo para comparar visualmente su evolución${activeKeys.length > 1 ? " (normalizados a % de variación mientras haya más de uno activo)" : ""}.`}
            </div>

            {activeKeys.length > 0 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid="macro-history-equities">
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <EquitiesChart bySeries={bySeries} activeKeys={activeKeys} absolute={absolute} height={90} small />
                </div>
            )}

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold" data-testid="macro-asof-equities">Dato: {fmtDate(ind.as_of)}</span>
            </div>
            {histOpen && (
                <EquitiesHistoryModal
                    ind={ind} bySeries={bySeries} activeKeys={activeKeys} absolute={absolute}
                    onToggleKey={toggle} onToggleAbsolute={toggleAbsolute}
                    onClose={() => setHistOpen(false)}
                />
            )}
        </div>
    );
};

const OilAverageCard = ({ ind, years, onYearsChange }) => {
    const Icon = ICONS[ind.key] || Droplet;
    const dial = ind.dial || { min: 1, max: 20, default: 4 };
    const history = ind.history || [];
    const current = ind.value;
    const [histOpen, setHistOpen] = useState(false);

    const avg = React.useMemo(() => oilAverage(history, years), [history, years]);

    const diffPct = avg != null && current != null && avg !== 0 ? ((current - avg) / avg) * 100 : null;
    const expensive = diffPct != null && diffPct > 0;
    const diffColor = diffPct == null ? "#4A4A4A" : expensive ? "#B32A22" : "#1F7A3D";
    const spanYears = history.length ? (history.length / 12) : 0;
    const capped = years * 12 > history.length;

    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={`${ind.description}\n\nFuente: ${ind.source} · ${ind.frequency}`}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-info-${ind.key}`} aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                {history.length > 1 && (
                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-expand-${ind.key}`} aria-label="Ampliar histórico" title="Ampliar">
                        <Maximize2 size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid={`macro-value-${ind.key}`}>
                    {fmtVal(current)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit} · actual</span>
            </div>

            {history.length > 1 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-history-${ind.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <IndHistoryChart history={history} unit={ind.unit} height={90} small />
                </div>
            )}

            <div className="mt-3 border-t border-black/10 pt-2.5">
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-[#7A7A7A] font-medium">Media últimos</span>
                    <span className="text-[13px] font-serif text-[#052049] tabular-nums" data-testid="oil-avg-years">{years} {years === 1 ? "año" : "años"}</span>
                </div>
                <Slider
                    min={dial.min} max={dial.max} step={1}
                    value={[years]} onValueChange={(v) => onYearsChange(v[0])}
                    className="my-2" data-testid="oil-avg-slider"
                />
                <div className="flex items-baseline justify-between mt-2.5">
                    <span className="text-[11px] text-[#7A7A7A]">Media del periodo</span>
                    <span className="font-serif tabular-nums text-2xl text-[#052049]" data-testid="oil-avg-value">
                        {avg != null ? nf.format(avg) : "—"} <span className="text-xs text-[#4A4A4A]">USD/barril</span>
                    </span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-[#7A7A7A]">Actual vs media</span>
                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: diffColor }} data-testid="oil-avg-diff">
                        {diffPct != null ? `${diffPct > 0 ? "+" : ""}${nf.format(diffPct)}% · ${expensive ? "caro" : "barato"}` : "—"}
                    </span>
                </div>
                {capped && <div className="text-[10px] text-[#B8860B] mt-1.5">Solo hay ~{Math.floor(spanYears)} años de histórico; se usa todo el disponible.</div>}
            </div>

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold">Dato: {fmtDate(ind.as_of)}</span>
            </div>
            {histOpen && <IndHistoryModal ind={ind} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

const EnergyMixCard = ({ ind }) => {
    const comps = ind.components || [];
    const maxPct = comps.length ? Math.max(...comps.map((c) => c.pct)) : 100;
    const history = ind.history || [];
    const [histOpen, setHistOpen] = useState(false);
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid="macro-card-energy_mix">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Zap size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={`${ind.description}\n\n${ind.interpretation}\n\nFuente: ${ind.source} · ${ind.frequency}${ind.note ? `\n\n${ind.note}` : ""}`}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="macro-info-energy_mix" aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                {history.length > 1 && (
                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="macro-expand-energy_mix" aria-label="Ampliar histórico" title="Ampliar">
                        <Maximize2 size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid="macro-value-energy_mix">{fmtVal(ind.value)}</span>
                <span className="text-xs text-[#4A4A4A] font-medium">% petróleo + gas{ind.estimated ? " · est." : ""}</span>
            </div>
            <div className="text-[11px] text-[#7A7A7A] mt-1.5">Cuota de petróleo + gas natural sobre el total de energía primaria</div>

            {history.length > 1 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid="macro-history-energy_mix">
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <IndHistoryChart history={history} unit={ind.unit} height={90} small tickFormatter={yearLabel} />
                </div>
            )}

            <div className="mt-3 border-t border-black/10 pt-2 space-y-1" data-testid="macro-breakdown-energy_mix">
                <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Desglose por fuente</div>
                {comps.map((c) => (
                    <div key={c.label} data-testid={`energy-src-${c.label}`}>
                        <div className="flex items-center justify-between text-[11px] tabular-nums">
                            <span className="text-[#4A4A4A]">{c.label}</span>
                            <span className="text-[#052049] font-medium">{fmtVal(c.pct)}%</span>
                        </div>
                        <div className="h-1 bg-black/10 mt-0.5"><div className="h-full bg-[#052049]" style={{ width: `${(c.pct / maxPct) * 100}%` }} /></div>
                    </div>
                ))}
            </div>

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold">{ind.estimated ? "Estimado" : "Datos"}: {ind.as_of}</span>
            </div>
            {histOpen && <IndHistoryModal ind={ind} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

// Optional overlays on the high yield spread chart (marcar/desmarcar). The NASDAQ 100 is the
// equities card's own series (FRED NASDAQ100, already downloaded); the 10-year Treasury comes
// with the spread indicator (FRED DGS10). The 10-year shares the left axis with the spread
// (both in %); the NASDAQ 100 (points) goes on its own right axis.
const HY_OVERLAYS = [
    { k: "nasdaq", label: "Nasdaq 100", color: "#1F7A3D", unit: "puntos", axis: "right" },
    { k: "us10y", label: "Bono EE. UU. 10 años", color: "var(--chart-blue)", unit: "%", axis: "left" },
];
const HY_COLOR = "#B32A22";

// Rows {date, value, nasdaq?, us10y?} with EVERY series cut to the same date window (the
// overlap of the active ones), so a longer series never squashes the others.
const hyRows = (history, overlays, active) => {
    const series = [["value", history || []], ...HY_OVERLAYS.filter((o) => active.includes(o.k) && overlays[o.k]?.history?.length).map((o) => [o.k, overlays[o.k].history])];
    const lo = series.reduce((m, [, h]) => (h.length && h[0].date > m ? h[0].date : m), "");
    const hi = series.reduce((m, [, h]) => (h.length && h[h.length - 1].date < m ? h[h.length - 1].date : m), "9999");
    const byDate = new Map();
    for (const [k, h] of series) {
        for (const p of h) {
            if (p.date < lo || p.date > hi || p.value == null) continue;
            if (!byDate.has(p.date)) byDate.set(p.date, { date: p.date });
            byDate.get(p.date)[k] = p.value;
        }
    }
    return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
};

const HYSpreadTip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className={CHART_TIP} data-testid="hy-spread-tooltip">
            <div className="font-semibold">{dLabel(p.date, { full: true })}</div>
            {p.value != null && <div className="tabular-nums" style={{ color: "#F08A82" }}>Spread: {nf.format(p.value)} p.p.</div>}
            {p.us10y != null && <div className="tabular-nums" style={{ color: "var(--chart-blue)" }}>Bono 10 años: {nf.format(p.us10y)} %</div>}
            {p.nasdaq != null && <div className="tabular-nums" style={{ color: "#5BC48A" }}>Nasdaq 100: {nf.format(p.nasdaq)} puntos</div>}
        </div>
    );
};

const HYOverlayButtons = ({ overlays, active, onToggle, testIdPrefix = "hy-overlay" }) => {
    const available = HY_OVERLAYS.filter((o) => overlays[o.k]?.history?.length);
    if (!available.length) return null;
    return (
        <div className="flex flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-buttons`}>
            {available.map((o) => {
                const on = active.includes(o.k);
                return (
                    <HoverTip key={o.k} text={`${on ? "Quitar" : "Superponer"} ${o.label} en el gráfico del spread (${o.axis === "right" ? "eje derecho, en puntos" : "mismo eje, en %"}). Fuente: ${overlays[o.k].source}.`}>
                        <button onClick={() => onToggle(o.k)} aria-pressed={on}
                            className="text-[10px] uppercase tracking-wide px-2 py-1 border transition-colors"
                            style={on ? { borderColor: o.color, backgroundColor: o.color, color: "#fff" } : { borderColor: "rgba(0,0,0,0.2)", color: "#4A4A4A" }}
                            data-testid={`${testIdPrefix}-btn-${o.k}`}>
                            {o.label}
                        </button>
                    </HoverTip>
                );
            })}
        </div>
    );
};

const HighYieldSpreadChart = ({ history, overlays = {}, active = [], height, small }) => {
    const rows = React.useMemo(() => hyRows(history, overlays, active), [history, overlays, active]);
    if (!rows.length) return null;
    const right = active.includes("nasdaq") && rows.some((r) => r.nasdaq != null);
    const fs = small ? 8 : 11;
    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={rows} margin={{ top: 4, right: small ? 4 : (right ? 4 : 40), left: small ? -22 : 0, bottom: 0 }}>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <XAxis dataKey="date" tickFormatter={qLabel} tick={{ fontSize: fs, fill: "#7A7A7A" }} interval={Math.max(0, Math.ceil(rows.length / (small ? 4 : 10)) - 1)} axisLine={{ stroke: "#00000022" }} tickLine={false} minTickGap={small ? 12 : 20} />
                <YAxis yAxisId="left" tick={{ fontSize: fs, fill: "#7A7A7A" }} width={small ? 26 : 40} axisLine={false} tickLine={false} domain={["auto", "auto"]} tickFormatter={(v) => nf.format(v)} />
                {right && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: fs, fill: "#1F7A3D" }} width={small ? 30 : 52} axisLine={false} tickLine={false} domain={["auto", "auto"]} tickFormatter={(v) => (v >= 1000 ? `${nf.format(Math.round(v / 1000))}k` : nf.format(v))} />}
                <RTooltip content={<HYSpreadTip />} />
                <Line yAxisId="left" type="monotone" dataKey="value" stroke={HY_COLOR} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                {active.includes("us10y") && <Line yAxisId="left" type="monotone" dataKey="us10y" stroke="var(--chart-blue)" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />}
                {right && <Line yAxisId="right" type="monotone" dataKey="nasdaq" stroke="#1F7A3D" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

const HighYieldSpreadModal = ({ ind, history, overlays, active, onToggle, onClose }) => {
    const ref = React.useRef(null);
    const [busy, setBusy] = useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, "spread-high-yield-evolucion"); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="hy-spread-modal">
            <div className="bg-white border-2 border-[#052049] w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <ShieldAlert size={22} className="shrink-0" /> <span className="truncate">Spread de high yield · Evolución</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title="Spread de high yield · VegaValor" testidPrefix="hy-spread-share"
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", "Spread de high yield")} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="hy-spread-download-jpg" title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="hy-spread-modal-close" aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                <div className="mb-2"><HYOverlayButtons overlays={overlays} active={active} onToggle={onToggle} testIdPrefix="hy-modal-overlay" /></div>
                <div ref={ref}>
                    <HighYieldSpreadChart history={history} overlays={overlays} active={active} height={420} />
                </div>
                <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed">
                    {ind.description} Fuente: {ind.source} · {ind.frequency}.
                    {active.length > 0 && ` Superpuesto: ${HY_OVERLAYS.filter((o) => active.includes(o.k) && overlays[o.k]).map((o) => `${o.label} (${overlays[o.k].source})`).join(", ")}; todas las series en el mismo rango de fechas.`}
                </p>
            </div>
        </div>
    );
};

const HighYieldSpreadCard = ({ ind, nasdaq }) => {
    const Icon = ICONS[ind.key] || ShieldAlert;
    const history = ind.history || [];
    const [histOpen, setHistOpen] = useState(false);
    const [active, setActive] = useState([]);  // overlays shown: "nasdaq", "us10y"
    const overlays = React.useMemo(() => ({
        ...(nasdaq?.history?.length ? { nasdaq: { ...nasdaq, source: "FRED · NASDAQ100" } } : {}),
        ...(ind.overlays || {}),
    }), [nasdaq, ind.overlays]);
    const toggle = (k) => setActive((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={`${ind.description}\n\n${ind.interpretation}\n\nFuente: ${ind.source} · ${ind.frequency}`}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-info-${ind.key}`} aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="hy-spread-expand-btn" aria-label="Ampliar histórico" title="Ampliar">
                    <Maximize2 size={14} />
                </button>
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid={`macro-value-${ind.key}`}>
                    {fmtVal(ind.value)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit}</span>
            </div>
            <div className="text-[11px] text-[#7A7A7A] mt-1.5">{ind.interpretation}</div>

            {history.length > 1 && (
                <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-history-${ind.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución</div>
                    <HighYieldSpreadChart history={history} overlays={overlays} active={active} height={90} small />
                </div>
            )}
            <div className="mt-2"><HYOverlayButtons overlays={overlays} active={active} onToggle={toggle} /></div>

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold" data-testid={`macro-asof-${ind.key}`}>Dato: {fmtDate(ind.as_of)}</span>
            </div>
            {histOpen && <HighYieldSpreadModal ind={ind} history={history} overlays={overlays} active={active} onToggle={toggle} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

const DEBT_SERIES = [
    { key: "debt", label: "Deuda pública", color: "#B32A22", axis: "left" },
    { key: "gdp", label: "PIB", color: "#1F7A3D", axis: "left" },
    { key: "ratio", label: "Deuda/PIB", color: "var(--chart-blue)", axis: "right" },
];

const DebtTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className={CHART_TIP} data-testid="debt-tooltip">
            <div className="font-semibold mb-1">{qLabel(label)}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex justify-between gap-3 tabular-nums">
                    <span style={{ color: p.color }}>{p.name}</span>
                    <span>{p.value == null ? "—" : nf.format(p.value)}<span className="text-[#9CA3AF]"> {p.dataKey === "ratio" ? "%" : "mM$"}</span></span>
                </div>
            ))}
        </div>
    );
};

const DebtChart = ({ data, height, small }) => (
    <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: small ? 4 : 6, left: small ? -14 : 4, bottom: 0 }}>
            <CartesianGrid stroke="#00000010" vertical={false} />
            <XAxis dataKey="date" tickFormatter={qLabel} tick={{ fontSize: small ? 9 : 11, fill: "#7A7A7A" }} interval={small ? 6 : 3} axisLine={{ stroke: "#00000022" }} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: small ? 9 : 11, fill: "#7A7A7A" }} width={small ? 32 : 48} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: small ? 9 : 11, fill: "var(--chart-blue)" }} width={small ? 30 : 44} axisLine={false} tickLine={false} domain={["auto", "auto"]} tickFormatter={(v) => `${nf.format(v)}%`} />
            <RTooltip content={<DebtTip />} />
            {!small && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {DEBT_SERIES.map((s) => (
                <Line key={s.key} yAxisId={s.axis} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
            ))}
        </ComposedChart>
    </ResponsiveContainer>
);

const DebtModal = ({ points, onClose }) => {
    const ref = React.useRef(null);
    const [busy, setBusy] = useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, "deuda-publica-pib-eeuu"); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="debt-modal">
            <div className="bg-white border-2 border-[#052049] w-full max-w-5xl p-4 sm:p-5 flex flex-col max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <Landmark size={22} className="text-[#052049] shrink-0" /> <span className="truncate">Deuda pública vs PIB · 10 años</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title="Deuda pública vs PIB · VegaValor" testidPrefix="debt-modal-share"
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", "Deuda pública vs PIB")} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="debt-modal-download-jpg" title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="debt-modal-close" aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                <div ref={ref} className="w-full h-[50vh] min-h-[240px] max-h-[460px] shrink-0">
                    <DebtChart data={points} height="100%" />
                </div>
                <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed shrink-0">
                    Deuda pública total y PIB nominal de EEUU (<strong>eje izquierdo</strong>, miles de M$), y su ratio Deuda/PIB (<strong>eje derecho</strong>, %). Datos trimestrales oficiales, sin extrapolar.
                </p>
            </div>
        </div>
    );
};

const DebtCard = ({ ind }) => {
    const [open, setOpen] = useState(false);
    const points = ind.history || [];
    if (!points.length) return null;
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid={`macro-card-${ind.key}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Landmark size={13} className="text-[#052049]" /> {ind.label}
                    <HoverTip text={`${ind.description}\n\n${ind.interpretation}\n\nFuente: ${ind.source} · ${ind.frequency}`}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid={`macro-info-${ind.key}`} aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                <button onClick={() => setOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="debt-expand-btn" aria-label="Ampliar histórico" title="Ampliar">
                    <Maximize2 size={14} />
                </button>
            </div>

            <div className="flex items-baseline gap-1.5">
                <span className="font-serif tabular-nums text-3xl text-[#052049] leading-none" data-testid={`macro-value-${ind.key}`}>
                    {fmtVal(ind.value)}
                </span>
                <span className="text-xs text-[#4A4A4A] font-medium">{ind.unit}</span>
            </div>
            <div className="text-[11px] text-[#7A7A7A] mt-1.5">{ind.interpretation}</div>

            <div className="mt-3 border-t border-black/10 pt-2" data-testid={`macro-history-${ind.key}`}>
                <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Evolución · 10 años</div>
                <DebtChart data={points} height={110} small />
            </div>

            <div className="mt-auto pt-2.5 flex items-center justify-between text-[11px]">
                <span className="uppercase tracking-wide text-[#7A7A7A] font-medium">{ind.frequency}</span>
                <span className="tabular-nums text-[#052049] font-semibold" data-testid={`macro-asof-${ind.key}`}>Dato: {fmtDate(ind.as_of)}</span>
            </div>
            {open && <DebtModal points={points} onClose={() => setOpen(false)} />}
        </div>
    );
};

const DEFENSIVE = ["KO", "PG", "JNJ", "WM"];
const GROWTH = ["NVDA", "PLTR", "TSLA", "SHOP"];

const CoefficientGauge = ({ c }) => {
    const cx = 130, cy = 122, R = 96;
    const val = Math.max(0, Math.min(2, c));         // clamp to [0,2]
    const rot = (val - 1) * 90;                       // 1→0° (up), 0→-90° (left), 2→+90° (right)
    return (
        <svg viewBox="0 0 260 150" className="w-full max-w-[280px]" data-testid="coef-gauge">
            <defs>
                <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#B32A22" />
                    <stop offset="25%" stopColor="#E8833A" />
                    <stop offset="50%" stopColor="#9AA0A6" />
                    <stop offset="75%" stopColor="#7BC47F" />
                    <stop offset="100%" stopColor="#1F7A3D" />
                </linearGradient>
            </defs>
            <path d={`M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy}`} fill="none" stroke="url(#gaugeGrad)" strokeWidth="15" strokeLinecap="round" />
            <text x={cx - R} y={cy + 16} textAnchor="middle" className="fill-[#B32A22]" fontSize="10" fontWeight="700">0 · caro</text>
            <text x={cx} y="18" textAnchor="middle" className="fill-[#6A6A6A]" fontSize="10" fontWeight="700">1</text>
            <text x={cx + R} y={cy + 16} textAnchor="middle" className="fill-[#1F7A3D]" fontSize="10" fontWeight="700">2 · barato</text>
            <g transform={`rotate(${rot} ${cx} ${cy})`}>
                <line x1={cx} y1={cy} x2={cx} y2={cy - R + 8} stroke="var(--brand)" strokeWidth="3" strokeLinecap="round" />
            </g>
            <circle cx={cx} cy={cy} r="6" fill="var(--brand)" />
        </svg>
    );
};

const dLabel = (iso, opts) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: opts?.full ? "numeric" : "2-digit" });
    } catch { return iso; }
};

const CoefHistTip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className={CHART_TIP} data-testid="coef-hist-tooltip">
            <div className="font-semibold">{dLabel(p.date, { full: true })}</div>
            <div className="tabular-nums">Coef.: {nf.format(p.c)} · {p.c > 1 ? "barato" : p.c < 1 ? "caro" : "neutro"}</div>
        </div>
    );
};

const CoefHistoryChart = ({ history, height, small, logScale }) => {
    const gid = React.useId();
    if (!history?.length) return null;
    const data = history.map((h) => ({ ...h, above: h.c >= 1 ? h.c : 1, below: h.c <= 1 ? h.c : 1 }));
    // Logarithmic (opt-in): a spike like 2020's (C reached ~9.75) still shows at its
    // real height — nothing hidden — but log compresses that outlier's share of the
    // vertical space instead of a linear scale stretching the whole axis to fit it,
    // which squeezes the "caro" (below 1) range into a sliver. It also fits the
    // coefficient's own nature better: a multiplicative ratio around 1 (2x vs 0.5x
    // are symmetric opposites), which linear doesn't represent evenly.
    const yAxisProps = logScale
        ? { scale: "log", domain: [(min) => Math.max(0.1, Math.min(0.5, min)), (max) => Math.max(1.1, max)] }
        : { domain: [(min) => Math.min(0.9, min), (max) => Math.max(1.1, max)] };
    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={data} margin={{ top: 6, right: small ? 4 : 40, left: small ? -22 : 0, bottom: 0 }}>
                <defs>
                    <linearGradient id={`coefGreen-${gid}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--cheap)" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="var(--cheap)" stopOpacity={0.04} />
                    </linearGradient>
                    <linearGradient id={`coefRed-${gid}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--expensive)" stopOpacity={0.04} />
                        <stop offset="100%" stopColor="var(--expensive)" stopOpacity={0.5} />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <Area type="monotone" dataKey="above" baseValue={1} stroke="none" fill={`url(#coefGreen-${gid})`} isAnimationActive={false} dot={false} activeDot={false} />
                <Area type="monotone" dataKey="below" baseValue={1} stroke="none" fill={`url(#coefRed-${gid})`} isAnimationActive={false} dot={false} activeDot={false} />
                <XAxis dataKey="date" tickFormatter={qLabel} tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} interval={Math.max(0, Math.ceil(history.length / (small ? 4 : 10)) - 1)} axisLine={{ stroke: "#00000022" }} tickLine={false} minTickGap={small ? 12 : 20} />
                <YAxis {...yAxisProps} tick={{ fontSize: small ? 8 : 11, fill: "#7A7A7A" }} width={small ? 26 : 40} axisLine={false} tickLine={false} tickFormatter={(v) => nf.format(v)} />
                <ReferenceLine y={1} stroke="#6A6A6A" strokeDasharray="4 3" label={small ? null : { value: "1 · neutro", position: "right", fontSize: 10, fill: "#6A6A6A" }} />
                <RTooltip content={<CoefHistTip />} />
                <Line type="monotone" dataKey="c" stroke="var(--brand)" strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

const CoefHistoryModal = ({ history, logScale, onToggleLogScale, onClose }) => {
    const ref = React.useRef(null);
    const [busy, setBusy] = React.useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, "historico-coeficiente-macro"); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="coef-history-modal">
        <div className="bg-white border-2 border-[#052049] w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0"><LineChartIcon size={22} className="shrink-0" /> <span className="truncate">Histórico del coeficiente · 10 años</span></h2>
                <div className="flex items-center gap-3 shrink-0">
                    <button
                        onClick={onToggleLogScale}
                        aria-pressed={logScale}
                        title="Escala logarítmica: comprime visualmente los picos extremos (p. ej. el de 2020) para ver mejor las variaciones por debajo de 1, sin ocultar el valor real alcanzado"
                        className="text-[10px] uppercase tracking-wide px-2 py-1 border transition-colors"
                        style={logScale ? { borderColor: "#052049", backgroundColor: "#052049", color: "#fff" } : { borderColor: "rgba(0,0,0,0.2)", color: "#4A4A4A" }}
                        data-testid="coef-history-modal-log-toggle"
                    >
                        Escala log
                    </button>
                    <ShareMenu size="md" title="Histórico del coeficiente · VegaValor" testidPrefix="coef-history-share"
                        createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", "Histórico del coeficiente")} />
                    <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="coef-history-download-jpg" title="Descargar en JPG">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                    </button>
                    <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="coef-history-modal-close" aria-label="Cerrar"><X size={20} /></button>
                </div>
            </div>
            <div ref={ref}>
                <CoefHistoryChart history={history} height={420} logScale={logScale} />
            </div>
            <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed">
                Los años más antiguos son una reconstrucción trimestral con los mismos supuestos que la fórmula en vivo (el peso de los fondos monetarios institucionales, sin archivo histórico propio, se extrapola de forma geométrica a partir de su peso actual). Desde hoy en adelante, el histórico crece con un punto real cada día. <span className="text-[#1F7A3D] font-semibold">Zona verde</span> (por encima de 1) = mercado barato; <span className="text-[#B32A22] font-semibold">zona roja</span> (por debajo de 1) = caro.
                {logScale && " Escala logarítmica activada: los picos extremos (como el de 2020) se comprimen visualmente sin ocultar su valor real, para ver mejor las variaciones por debajo de 1."}
            </p>
        </div>
    </div>
    );
};

const CoefficientCard = ({ byKey, oilYears, coefHistory }) => {
    const [histOpen, setHistOpen] = useState(false);
    const [logScale, setLogScale] = useState(false);
    const res = computeCoefficient(byKey, oilYears);
    if (!res) {
        return (
            <div className="border border-black/20 bg-white p-5 mb-4" data-testid="coef-card">
                <div className="overline text-[#B32A22] mb-1">Coeficiente de mercado</div>
                <div className="text-sm text-[#7A7A7A]">Faltan datos para calcular el coeficiente.</div>
            </div>
        );
    }
    const { C, terms, vals, hy } = res;
    const cheap = C > 1, expensive = C < 1;
    const pctToOne = (C - 1) * 100;
    const absPct = `${nf.format(Math.abs(pctToOne))}%`;
    const zoneColor = expensive ? "#B32A22" : cheap ? "#1F7A3D" : "#6A6A6A";
    const verdict = expensive ? "Mercado CARO" : cheap ? "Mercado BARATO" : "Neutral";
    const advice = expensive ? "Sesgo defensivo / efectivo" : cheap ? "Sesgo crecimiento / agresivo" : "Equilibrado";
    const hyStressed = hy?.value != null && hy.value >= HY_CAP;

    const factorRows = [
        ["m70", "Renta variable (est.)", vals.m70], ["m71", "PIB (est.)", vals.m71], ["m72", "M3 proxy", vals.m72],
        ["m73", "Tipo FED", vals.m73], ["m74", "Inflación", vals.m74], ["m75", "Productividad", vals.m75],
        ["m76", "Precio petróleo", vals.m76], ["m77", `Media petróleo (${oilYears}a)`, vals.m77],
        ["m78", "Mix petróleo+gas", vals.m78], ["m79", "Spread high yield", vals.m79],
    ];

    return (
        <div className="border-2 border-[#052049] bg-white p-5 mb-4" data-testid="coef-card">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="overline text-[#B32A22] flex items-center gap-2">
                    Coeficiente de mercado
                    {hyStressed && (
                        <HoverTip text={`Spread de high yield en ${nf.format(hy.value)} p.p., por encima del umbral de estrés (${nf.format(HY_CAP)} p.p.).\n\nA partir de aquí el coeficiente cae con más fuerza; pasado este punto sigue bajando pero ya más despacio.`}>
                            <span className="inline-flex items-center gap-1 text-[#B32A22] font-semibold normal-case tracking-normal text-[11px] animate-pulse cursor-help" data-testid="coef-hy-warning">
                                <AlertTriangle size={13} /> Estrés crediticio
                            </span>
                        </HoverTip>
                    )}
                </div>
                <HoverTip text={"C = (PIB / (Renta variable − M3 proxy)) × (1 − (Tipo FED + Inflación)/100) × (Productividad/100) × (1 − ((Precio petróleo − Media petróleo) × (Mix petróleo+gas/10000))) × t5\n\nt5 (spread high yield): curva continua y simétrica alrededor de 3,0 p.p. (ahí no afecta). Hacia spreads más altos el coeficiente baja, acelerando hasta 3,5 p.p. (su punto más brusco) y después sigue bajando pero más despacio. Hacia spreads más bajos sube igual de rápido, con el máximo en 2,5 p.p. Depende solo del valor actual del spread.\n\nPor debajo de 1 = mercado caro (defensivas/efectivo). Por encima de 1 = mercado barato (crecimiento/agresivas). La media del petróleo (m77) usa los años del dial de la ficha de Petróleo."}>
                    <button className="text-[#9A9A9A] hover:text-[#052049]" data-testid="coef-info" aria-label="Fórmula"><Info size={15} /></button>
                </HoverTip>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                <div className="flex flex-col items-center">
                    <CoefficientGauge c={C} />
                    <div className="flex items-baseline gap-1.5 mt-1">
                        <span className="font-serif tabular-nums text-5xl leading-none" style={{ color: zoneColor }} data-testid="coef-value">{nf.format(C)}</span>
                        <span className="text-sm font-semibold tabular-nums" style={{ color: zoneColor }} data-testid="coef-pct-to-one" title="Diferencia hasta 1 (neutro). En 1 = 0%.">
                            {`${C - 1 > 0 ? "+" : ""}${nf.format((C - 1) * 100)}%`}
                        </span>
                    </div>
                    <div className="mt-1 text-sm font-semibold" style={{ color: zoneColor }} data-testid="coef-verdict">{verdict} · {advice}</div>
                    {coefHistory?.length > 1 && (
                        <div className="w-full mt-3 border-t border-black/10 pt-2" data-testid="coef-history-mini">
                            <div className="flex items-center justify-between mb-0.5 gap-1.5">
                                <span className="text-[10px] uppercase tracking-wide text-[#9A9A9A]">Histórico · 10 años</span>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        onClick={() => setLogScale((v) => !v)}
                                        aria-pressed={logScale}
                                        title="Escala logarítmica: comprime visualmente los picos extremos (p. ej. el de 2020) para ver mejor las variaciones por debajo de 1, sin ocultar el valor real alcanzado"
                                        className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 border transition-colors"
                                        style={logScale ? { borderColor: "#052049", backgroundColor: "#052049", color: "#fff" } : { borderColor: "rgba(0,0,0,0.2)", color: "#9A9A9A" }}
                                        data-testid="coef-log-scale-toggle"
                                    >
                                        Log
                                    </button>
                                    <button onClick={() => setHistOpen(true)} className="text-[#9A9A9A] hover:text-[#052049]" data-testid="coef-history-expand-btn" aria-label="Ampliar histórico" title="Ampliar">
                                        <Maximize2 size={13} />
                                    </button>
                                </div>
                            </div>
                            <CoefHistoryChart history={coefHistory} height={84} small logScale={logScale} />
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className={`border p-2.5 ${expensive ? "border-[#B32A22] bg-[#FBE9E7]" : "border-black/15 opacity-60"}`} data-testid="coef-defensive">
                            <div className="flex items-center justify-between gap-1 mb-1">
                                <span className="text-[11px] uppercase tracking-wide font-semibold text-[#B32A22]">Caro → Defensivas/efectivo</span>
                                {expensive && <span className="font-semibold tabular-nums text-[11px] text-[#B32A22]" data-testid="coef-defensive-pct">{absPct}</span>}
                            </div>
                            <div className="text-[13px] text-[#4A4A4A] tabular-nums">{DEFENSIVE.join(" · ")}</div>
                        </div>
                        <div className={`border p-2.5 ${cheap ? "border-[#1F7A3D] bg-[#E9F5EC]" : "border-black/15 opacity-60"}`} data-testid="coef-growth">
                            <div className="flex items-center justify-between gap-1 mb-1">
                                <span className="text-[11px] uppercase tracking-wide font-semibold text-[#1F7A3D]">Barato → Crecimiento/smallcaps</span>
                                {cheap && <span className="font-semibold tabular-nums text-[11px] text-[#1F7A3D]" data-testid="coef-growth-pct">{absPct}</span>}
                            </div>
                            <div className="text-[13px] text-[#4A4A4A] tabular-nums">{GROWTH.join(" · ")}</div>
                        </div>
                    </div>
                    <div className="border-t border-black/10 pt-2">
                        <div className="text-[10px] uppercase tracking-wide text-[#9A9A9A] mb-1">Valores usados</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
                            {factorRows.map(([code, label, v]) => (
                                <div key={code} className="flex justify-between gap-1">
                                    <span className="text-[#7A7A7A] truncate" title={label}>{label}</span>
                                    <span className="text-[#052049] font-medium">{v == null ? "—" : nf.format(v)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {histOpen && <CoefHistoryModal history={coefHistory} logScale={logScale} onToggleLogScale={() => setLogScale((v) => !v)} onClose={() => setHistOpen(false)} />}
        </div>
    );
};

const qLabel = (iso) => {
    if (iso === "est") return "Est.";
    const [y, m] = iso.split("-");
    const q = Math.ceil(parseInt(m, 10) / 3);
    return `${q}T${y.slice(2)}`;
};

const TREND_SERIES = [
    { key: "equities", label: "Renta variable", color: "var(--chart-blue)", axis: "left" },
    { key: "gdp", label: "PIB", color: "#1F7A3D", axis: "left" },
    { key: "diff", label: "RV − PIB", color: "#B8860B", axis: "left" },
    { key: "productivity", label: "Productividad", color: "#B32A22", axis: "right" },
];

const mkDot = (color) => (props) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null || !payload?.est) return <g key={index} />;
    return <circle key={index} cx={cx} cy={cy} r={4.5} fill="#fff" stroke={color} strokeWidth={2} />;
};

const TrendTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const est = payload[0]?.payload?.est;
    return (
        <div className={CHART_TIP} data-testid="trend-tooltip">
            <div className="font-semibold mb-1">{qLabel(label)}{est ? " · estimado" : ""}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex justify-between gap-3 tabular-nums">
                    <span style={{ color: p.color }}>{p.name}</span>
                    <span>{p.value == null ? "—" : nf.format(p.value)}<span className="text-[#9CA3AF]"> {p.dataKey === "productivity" ? "índ." : "mM$"}</span></span>
                </div>
            ))}
        </div>
    );
};

const TrendChart = ({ data, height, small }) => (
    <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 6, left: small ? -14 : 4, bottom: 0 }}>
            <CartesianGrid stroke="#00000010" vertical={false} />
            <XAxis dataKey="date" tickFormatter={qLabel} tick={{ fontSize: small ? 9 : 11, fill: "#7A7A7A" }} interval={small ? 6 : 3} axisLine={{ stroke: "#00000022" }} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: small ? 9 : 11, fill: "#7A7A7A" }} width={small ? 32 : 48} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: small ? 9 : 11, fill: "#B32A22" }} width={small ? 26 : 40} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
            <RTooltip content={<TrendTip />} />
            {!small && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {TREND_SERIES.map((s) => (
                <Line key={s.key} yAxisId={s.axis} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={mkDot(s.color)} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
            ))}
        </ComposedChart>
    </ResponsiveContainer>
);

// Official quarterly points + an estimated last point (live equities via selected index + live GDP).
const buildTrendData = (points, byKey) => {
    const base = (points || []).map((p) => ({ ...p }));
    const eNow = byKey?.equities?.value;  // total US market (Wilshire 5000 scale) — always real, no live estimate needed
    const gLive = byKey?.gdp?.live?.value;
    if (eNow != null && gLive != null && base.length) {
        const lastProd = [...base].reverse().find((p) => p.productivity != null)?.productivity ?? null;
        base.push({ date: "est", equities: eNow, gdp: gLive, diff: +(eNow - gLive).toFixed(1), productivity: lastProd, est: true });
    }
    return base;
};

const TrendCard = ({ points, byKey, onExpand }) => {
    const data = React.useMemo(() => buildTrendData(points, byKey), [points, byKey]);
    if (!data.length) return null;
    return (
        <div className="border border-black/20 bg-white p-3 flex flex-col" data-testid="macro-card-trend">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <LineChartIcon size={13} className="text-[#052049]" /> Evolución · 10 años
                    <HoverTip text={"Renta variable (mercado total EEUU, escala Wilshire 5000) y PIB, y su resta (RV − PIB), en el eje izquierdo (miles de M$); productividad laboral en el eje derecho (índice 2017=100). El último punto (Est.) usa el PIB estimado del trimestre en curso junto al dato real de renta variable.\n\nFuente: Marketstack · ITOT (escala Wilshire 5000), FRED · GDP, OPHNFB · Trimestral"}>
                        <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="macro-info-trend" aria-label="Más información">
                            <Info size={13} />
                        </button>
                    </HoverTip>
                </div>
                <button onClick={onExpand} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" data-testid="trend-expand-btn" aria-label="Ampliar gráfico" title="Ampliar">
                    <Maximize2 size={14} />
                </button>
            </div>
            <TrendChart data={data} height={150} small />
            <div className="text-[10px] text-[#9A9A9A] mt-1.5 leading-snug">
                Trimestral. El último punto (<span className="text-[#052049] font-semibold">Est.</span>) usa valores estimados. Eje dcho.: productividad.
            </div>
        </div>
    );
};

const TrendModal = ({ points, byKey, onClose }) => {
    const data = React.useMemo(() => buildTrendData(points, byKey), [points, byKey]);
    const ref = React.useRef(null);
    const [busy, setBusy] = React.useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, "evolucion-macro-10a"); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="trend-modal">
            <div className="bg-white border-2 border-[#052049] w-full max-w-5xl p-4 sm:p-5 flex flex-col max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <LineChartIcon size={22} className="text-[#052049] shrink-0" /> <span className="truncate">Evolución macro · 10 años (trimestral)</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title="Evolución macro · 10 años" testidPrefix="trend-modal-share"
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", "Evolución macro 10 años")} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" data-testid="trend-modal-download-jpg" title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" data-testid="trend-modal-close" aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                <div ref={ref} className="w-full h-[50vh] min-h-[240px] max-h-[460px] [@media(orientation:landscape)_and_(max-height:600px)]:h-[62vh] [@media(orientation:landscape)_and_(max-height:600px)]:min-h-0 shrink-0">
                    <TrendChart data={data} height="100%" />
                </div>
                <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed shrink-0 [@media(orientation:landscape)_and_(max-height:600px)]:hidden">
                    Renta variable (mercado total EEUU, escala Wilshire 5000), PIB y su resta (RV − PIB) en el <strong>eje izquierdo</strong> (miles de M$); Productividad en el <strong>eje derecho</strong> (índice 2017=100).
                    Valores oficiales trimestrales; el último punto marcado como <strong>Est.</strong> combina el dato real de renta variable con el PIB estimado del trimestre en curso.
                </p>
            </div>
        </div>
    );
};

export default function Macro() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [oilYears, setOilYears] = useState(4);  // shared oil dial value → feeds m77 of the coefficient
    const [trendOpen, setTrendOpen] = useState(false);

    const load = useCallback(async (refresh = false) => {
        if (refresh) setRefreshing(true); else setLoading(true);
        try {
            const d = await macroIndicators(refresh);
            setData(d);
            if (refresh) toast.success("Datos macro actualizados");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudieron cargar los datos macro");
        } finally {
            setLoading(false); setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    return (
        <div data-testid="macro-page">
            <div className="overline text-[#B32A22] mb-1">Contexto de mercado · EEUU</div>
            <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
                <h1 className="font-serif text-4xl sm:text-5xl text-[#052049] flex items-center gap-2">
                    <Globe2 size={32} className="text-[#052049]" />
                    <HoverTip text="Macro = contexto macroeconómico: los grandes indicadores de la economía (PIB, inflación, tipos de interés, empleo, energía…) que marcan el entorno en el que invierten todas las empresas. Ayuda a saber si el mercado está caro o barato en conjunto.">
                        <span className="cursor-help" data-testid="macro-title-tip">Macro</span>
                    </HoverTip>
                </h1>
                <button
                    onClick={() => load(true)}
                    disabled={refreshing || loading}
                    className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="macro-refresh-btn"
                >
                    {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Actualizar
                </button>
            </div>
            <p className="text-[#4A4A4A] max-w-3xl mb-6">
                Indicadores macroeconómicos de EEUU que enmarcan si la bolsa está cara o barata y en qué entorno
                financiero nos movemos. Pasa el ratón por el icono <Info size={13} className="inline -mt-0.5 text-[#052049]" /> de cada
                tarjeta para ver qué mide y en qué unidades. <strong>Fuente: FRED (Reserva Federal de St. Louis) y Marketstack (ETF ITOT, escala Wilshire 5000).</strong>
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-[#4A4A4A] py-10" data-testid="macro-loading">
                    <Loader2 size={18} className="animate-spin" /> Cargando datos de FRED…
                </div>
            ) : data?.indicators?.length ? (
                <>
                    <CoefficientCard
                        byKey={Object.fromEntries(data.indicators.map((i) => [i.key, i]))}
                        oilYears={oilYears}
                        coefHistory={data.coef_history}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="macro-grid">
                        {data.indicators.map((ind) => (
                            ind.key === "equities"
                                ? <EquitiesCard key={ind.key} ind={ind} />
                                : ind.key === "gdp"
                                    ? <LiveIndicatorCard key={ind.key} ind={ind} />
                                    : ind.key === "oil_avg"
                                        ? <OilAverageCard key={ind.key} ind={ind} years={oilYears} onYearsChange={setOilYears} />
                                        : ind.key === "energy_mix"
                                            ? <EnergyMixCard key={ind.key} ind={ind} />
                                            : ind.key === "high_yield_spread"
                                                ? <HighYieldSpreadCard key={ind.key} ind={ind} nasdaq={data.indicators.find((i) => i.key === "equities")?.history_by_series?.nasdaq} />
                                                : ind.key === "debt_to_gdp"
                                                    ? <DebtCard key={ind.key} ind={ind} />
                                                    : <MacroCard key={ind.key} ind={ind} />
                        ))}
                        {data.trend?.points?.length > 0 && (
                            <TrendCard
                                points={data.trend.points}
                                byKey={Object.fromEntries(data.indicators.map((i) => [i.key, i]))}
                                onExpand={() => setTrendOpen(true)}
                            />
                        )}
                    </div>
                    {trendOpen && (
                        <TrendModal
                            points={data.trend.points}
                            byKey={Object.fromEntries(data.indicators.map((i) => [i.key, i]))}
                            onClose={() => setTrendOpen(false)}
                        />
                    )}
                    <p className="text-[11px] text-[#9A9A9A] mt-4" data-testid="macro-updated">
                        Datos cacheados y refrescados periódicamente desde FRED.
                        {data.updated_at && ` Última sincronización: ${new Date(data.updated_at).toLocaleString("es-ES")}.`}
                    </p>
                </>
            ) : (
                <div className="text-[#9A9A9A] py-10" data-testid="macro-empty">Sin datos disponibles.</div>
            )}
        </div>
    );
}
